import { useInView } from 'framer-motion'
import React, { useEffect, useRef } from 'react'

import TagPill from '@/app/[locale]/(worlds)/components/TagPill'
import { Separator } from '@/components/ui/separator'

type FilterTag = {
  key: string
  label: string
}

type ContentTypeTag = {
  key: string
  label: string
  icon?: React.FC<React.SVGProps<SVGSVGElement>>
}

type ExploreViverseTagsProps = {
  contentTypeTags?: ContentTypeTag[]
  activeContentType?: string
  setActiveContentType?: (type: string) => void
  filterTags: FilterTag[]
  activeTag: string
  disabled?: boolean
  setActiveTag: (tag: string) => void
}

function scrollActiveTag(scroller: HTMLDivElement | null, target: HTMLButtonElement | null) {
  if (!scroller || !target) return

  const scrollerRect = scroller.getBoundingClientRect()
  const targetRect = target.getBoundingClientRect()

  const targetCenter =
    targetRect.left - scrollerRect.left + scroller.scrollLeft + targetRect.width / 2

  const nextScrollLeft = targetCenter - scroller.clientWidth / 2
  const maxScrollLeft = scroller.scrollWidth - scroller.clientWidth

  scroller.scrollTo({
    left: Math.max(0, Math.min(nextScrollLeft, maxScrollLeft)),
    behavior: 'smooth',
  })
}

export default function ExploreViverseTags({
  contentTypeTags,
  activeContentType,
  setActiveContentType,
  filterTags,
  activeTag,
  disabled = false,
  setActiveTag,
}: ExploreViverseTagsProps) {
  const contentTypeScrollerRef = useRef<HTMLDivElement>(null)
  const filterScrollerRef = useRef<HTMLDivElement>(null)
  const contentTypeTagRefs = useRef<Record<string, HTMLButtonElement | null>>({})
  const filterTagRefs = useRef<Record<string, HTMLButtonElement | null>>({})

  // scrollActiveTag reads getBoundingClientRect; this section is below the fold with
  // content-visibility:auto, so reading it while off-screen forces the skipped layout. Only centre the
  // active tag once the tag row is actually on screen.
  const tagsVisible = useInView(contentTypeScrollerRef, { margin: '200px', once: true })

  useEffect(() => {
    if (!tagsVisible || !contentTypeTags || !activeContentType) return

    scrollActiveTag(
      contentTypeScrollerRef.current,
      contentTypeTagRefs.current[activeContentType] ?? null,
    )
  }, [activeContentType, contentTypeTags, tagsVisible])

  useEffect(() => {
    if (!tagsVisible || !contentTypeTags) return

    scrollActiveTag(filterScrollerRef.current, filterTagRefs.current[activeTag] ?? null)
  }, [activeTag, contentTypeTags, tagsVisible])

  if (!contentTypeTags) {
    return filterTags.map((tag) => (
      <TagPill
        key={tag.key}
        label={tag.label}
        isActive={activeTag === tag.key}
        disabled={disabled}
        onClick={() => setActiveTag(tag.key)}
      />
    ))
  }

  return (
    <>
      <div
        ref={contentTypeScrollerRef}
        data-tag-scroller
        className="no-scrollbar flex gap-2 overflow-x-auto px-4 md:max-w-max md:shrink-0 md:px-0"
      >
        {contentTypeTags.map((tag) => (
          <TagPill
            key={tag.key}
            label={tag.label}
            isActive={activeContentType === tag.key}
            icon={tag.icon}
            buttonRef={(node) => {
              contentTypeTagRefs.current[tag.key] = node
            }}
            disabled={disabled}
            onClick={() => setActiveContentType?.(tag.key)}
          />
        ))}

        <Separator orientation="vertical" className="mx-1 h-5 shrink-0 self-center bg-white/10" />
      </div>

      <div
        ref={filterScrollerRef}
        data-tag-scroller
        className="no-scrollbar flex min-w-0 gap-2 overflow-x-auto px-4 md:flex-1 md:px-0"
      >
        {filterTags.map((tag) => (
          <TagPill
            key={tag.key}
            label={tag.label}
            isActive={activeTag === tag.key}
            buttonRef={(node) => {
              filterTagRefs.current[tag.key] = node
            }}
            disabled={disabled}
            onClick={() => setActiveTag(tag.key)}
          />
        ))}
      </div>
    </>
  )
}

export function ExploreViverseTagsSkeleton() {
  return Array.from({ length: 6 }).map((_, i) => (
    <div
      key={i}
      className="flex min-h-8 min-w-16 animate-pulse items-center justify-center rounded-[20px] border border-white/5 px-4 py-2 whitespace-nowrap"
    >
      <div className="bg-vive-ghost-white h-3 w-10 rounded" />
    </div>
  ))
}
